/**
 * Test Booking List
 * Displays and manages test bookings with lifecycle controls
 */

import { useState, useEffect, Fragment } from 'react'
import { Button } from '@/components/admin/ui/button'
import { Badge } from '@/components/admin/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/admin/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/admin/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card'
import { useToast } from '@/hooks/use-toast'
import { testBookingService, TestBooking } from '@/services/testBookingService'
import { emailTestService } from '@/services/emailTestService'
import { supabaseAdmin } from '@/integrations/supabase/client'
import { format } from 'date-fns'
import { parseDateOnly } from '@/lib/utils'
import { Loader2, Trash2, RotateCcw, ChevronRight, ChevronDown, Mail, XCircle, Link2, Save, ClipboardCheck, AlertTriangle, Clock, Check, X } from 'lucide-react'
import { Textarea } from '@/components/admin/ui/textarea'
import { BookingPipeline } from '@/components/admin/BookingPipeline'
import { BookingTimeline } from '@/components/admin/BookingTimeline'
import type { LifecycleStage } from '@/lib/booking-lifecycle'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/admin/ui/alert-dialog'
import { staffAuthHeaders } from '@/lib/api/staffAuthHeaders'

interface TestBookingListProps {
  adminEmail: string
}

// Lifecycle stages in order
const LIFECYCLE_STAGES = [
  { value: 'booked', label: 'Booked', color: 'bg-pink-200 text-pink-800' },
  { value: 'logistics_questionnaire_completed', label: 'Logistics Completed', color: 'bg-purple-200 text-purple-800' },
  { value: 'client_talent_communication_in_progress', label: 'Communication', color: 'bg-blue-200 text-blue-800' },
  { value: 'talent_5_day_check_in', label: '5-Day Check-In', color: 'bg-indigo-200 text-indigo-800' },
  { value: 'event_day', label: 'Event Day', color: 'bg-yellow-200 text-yellow-800' },
  { value: 'media_uploaded', label: 'Media Uploaded', color: 'bg-pink-400 text-pink-900' },
  { value: 'media_issue', label: 'Media Issue', color: 'bg-red-400 text-red-900' },
  { value: 'media_approved', label: 'Media Approved', color: 'bg-green-300 text-green-800' },
  { value: 'talent_paid', label: 'Talent Paid', color: 'bg-emerald-300 text-emerald-800' },
  { value: 'media_delivered_partial', label: 'Partial Delivery', color: 'bg-teal-300 text-teal-800' },
  { value: 'media_delivered_full', label: 'Full Delivery', color: 'bg-cyan-300 text-cyan-800' },
  { value: 'booking_complete', label: 'Complete', color: 'bg-green-500 text-white' },
]

export const TestBookingList = ({ adminEmail }: TestBookingListProps) => {
  const { toast } = useToast()
  const [bookings, setBookings] = useState<TestBooking[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [selectedStage, setSelectedStage] = useState<{ [key: string]: string }>({})
  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [bookingToCancel, setBookingToCancel] = useState<TestBooking | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [cancelReason, setCancelReason] = useState('')

  // Upload links state
  const [editingLinks, setEditingLinks] = useState<string | null>(null)
  const [uploadLinks, setUploadLinks] = useState<{
    [bookingId: string]: { photos: string; videos: string; raws: string }
  }>({})
  const [savingLinks, setSavingLinks] = useState<string | null>(null)

  // Job completion forms state
  const [jobCompletionForms, setJobCompletionForms] = useState<Record<string, any>>({})
  const [expandedJobForm, setExpandedJobForm] = useState<string | null>(null)

  useEffect(() => {
    loadBookings()
  }, [])

  const loadBookings = async () => {
    setLoading(true)
    try {
      const data = await testBookingService.getTestBookings()
      setBookings(data)
      // Initialize upload links state from fetched data
      const linksState: typeof uploadLinks = {}
      data.forEach((booking) => {
        linksState[booking.id] = {
          photos: booking.photos_upload_link || '',
          videos: booking.videos_upload_link || '',
          raws: booking.raws_upload_link || '',
        }
      })
      setUploadLinks(linksState)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load test bookings',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  // Fetch job completion form when row is expanded
  useEffect(() => {
    const fetchJobCompletionForm = async () => {
      if (!expandedRow) return
      if (jobCompletionForms[expandedRow]) return

      try {
        const { data, error } = await supabaseAdmin
          .from('job_completion_forms')
          .select(`
            *,
            submitted_by_user:users!job_completion_forms_submitted_by_fkey(full_name),
            photographer:photographers!job_completion_forms_photographer_id_fkey(
              id,
              user:users!photographers_user_id_fkey(full_name)
            )
          `)
          .eq('booking_id', expandedRow)
          .order('created_at', { ascending: false })

        if (error) {
          console.error('Error fetching job completion forms:', error)
          return
        }

        setJobCompletionForms(prev => ({
          ...prev,
          [expandedRow]: data || []
        }))
      } catch (error) {
        console.error('Error fetching job completion forms:', error)
      }
    }

    fetchJobCompletionForm()
  }, [expandedRow])

  const handleDelete = async (bookingId: string) => {
    setActionLoading(bookingId)
    try {
      await testBookingService.deleteTestBooking(bookingId)
      toast({
        title: 'Success',
        description: 'Test booking deleted'
      })
      loadBookings()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete booking',
        variant: 'destructive'
      })
    } finally {
      setActionLoading(null)
    }
  }

  const handleReset = async (bookingId: string) => {
    setActionLoading(bookingId)
    try {
      await testBookingService.resetBooking(bookingId)
      toast({
        title: 'Success',
        description: 'Booking reset to initial stage'
      })
      loadBookings()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to reset booking',
        variant: 'destructive'
      })
    } finally {
      setActionLoading(null)
    }
  }

  const handleAdvanceStage = async (bookingId: string) => {
    const stage = selectedStage[bookingId]
    if (!stage) {
      toast({
        title: 'Error',
        description: 'Please select a stage first',
        variant: 'destructive'
      })
      return
    }

    setActionLoading(bookingId)
    try {
      await testBookingService.advanceStage(bookingId, stage)
      toast({
        title: 'Success',
        description: `Booking advanced to ${LIFECYCLE_STAGES.find(s => s.value === stage)?.label}`
      })
      setSelectedStage(prev => ({ ...prev, [bookingId]: '' }))
      loadBookings()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to advance stage',
        variant: 'destructive'
      })
    } finally {
      setActionLoading(null)
    }
  }

  const handleTriggerEmail = async (bookingId: string, booking: TestBooking) => {
    setActionLoading(`email-${bookingId}`)
    try {
      // Send a test payment confirmation email with booking data
      await emailTestService.sendTestEmail('payment_confirmation', adminEmail, {
        customerName: booking.customer?.full_name || 'Test Customer',
        eventDate: booking.event_date,
        location: `${booking.location_city}, ${booking.location_state}`,
        amountPaid: booking.total_amount,
        bookingId: bookingId
      })
      toast({
        title: 'Success',
        description: `Test email sent to ${adminEmail}`
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send test email',
        variant: 'destructive'
      })
    } finally {
      setActionLoading(null)
    }
  }

  const handleCancel = async () => {
    if (!bookingToCancel) return
    if (!cancelReason.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a cancellation reason',
        variant: 'destructive'
      })
      return
    }

    setCancelling(true)
    try {
      const API_BASE = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${API_BASE}/api/testing/admin/bookings/${bookingToCancel.id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await staffAuthHeaders())
        },
        body: JSON.stringify({
          reason: cancelReason.trim()
        })
      })

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to cancel booking')
      }

      toast({
        title: 'Booking Cancelled',
        description: 'Cancellation emails have been sent to customer and photographer'
      })

      setCancelDialogOpen(false)
      setBookingToCancel(null)
      setCancelReason('')
      loadBookings()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to cancel booking',
        variant: 'destructive'
      })
    } finally {
      setCancelling(false)
    }
  }

  const handleSaveUploadLinks = async (bookingId: string) => {
    setSavingLinks(bookingId)
    try {
      const links = uploadLinks[bookingId]
      const { error } = await supabaseAdmin
        .from('bookings')
        .update({
          photos_upload_link: links.photos || null,
          videos_upload_link: links.videos || null,
          raws_upload_link: links.raws || null,
        })
        .eq('id', bookingId)

      if (error) throw error

      toast({
        title: 'Success',
        description: 'Upload links saved successfully',
      })
      setEditingLinks(null)
      await loadBookings()
    } catch (error) {
      console.error('Error saving upload links:', error)
      toast({
        title: 'Error',
        description: 'Failed to save upload links',
        variant: 'destructive',
      })
    } finally {
      setSavingLinks(null)
    }
  }

  const updateUploadLink = (bookingId: string, field: 'photos' | 'videos' | 'raws', value: string) => {
    setUploadLinks((prev) => ({
      ...prev,
      [bookingId]: {
        ...prev[bookingId],
        [field]: value,
      },
    }))
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'photographer': return 'Photographer'
      case 'videographer': return 'Videographer'
      case 'photo_and_video': return 'Photo & Video'
      default: return role
    }
  }

  const getStageColor = (stage: string | null) => {
    const found = LIFECYCLE_STAGES.find(s => s.value === stage)
    return found?.color || 'bg-gray-200 text-gray-800'
  }

  const getStageLabel = (stage: string | null) => {
    const found = LIFECYCLE_STAGES.find(s => s.value === stage)
    return found?.label || 'Unknown'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading test bookings...</span>
      </div>
    )
  }

  if (bookings.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No test bookings found.</p>
        <p className="text-sm mt-1">Create one using the form above.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10"></TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Photographer</TableHead>
            <TableHead>Event Date</TableHead>
            <TableHead>Stage</TableHead>
            <TableHead>Advance To</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.map(booking => (
            <Fragment key={booking.id}>
              <TableRow
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => setExpandedRow(expandedRow === booking.id ? null : booking.id)}
              >
                <TableCell className="w-10">
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                    {expandedRow === booking.id ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </TableCell>
                <TableCell>
                <div>
                  <p className="font-medium">{booking.customer?.full_name || 'Unknown'}</p>
                  <p className="text-sm text-muted-foreground">{booking.customer?.email}</p>
                </div>
              </TableCell>
              <TableCell>
                <div>
                  <p className="font-medium">{booking.photographer?.user?.full_name || 'Unknown'}</p>
                  <p className="text-sm text-muted-foreground">{booking.photographer?.user?.email}</p>
                </div>
              </TableCell>
              <TableCell>
                <div>
                  <p>{format(parseDateOnly(booking.event_date), 'MMM d, yyyy')}</p>
                  <p className="text-sm text-muted-foreground">{booking.event_time}</p>
                </div>
              </TableCell>
              <TableCell>
                <Badge className={getStageColor(booking.lifecycle_stage)}>
                  {getStageLabel(booking.lifecycle_stage)}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Select
                    value={selectedStage[booking.id] || ''}
                    onValueChange={(value) => setSelectedStage(prev => ({ ...prev, [booking.id]: value }))}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select stage..." />
                    </SelectTrigger>
                    <SelectContent>
                      {LIFECYCLE_STAGES.map(stage => (
                        <SelectItem key={stage.value} value={stage.value}>
                          {stage.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdvanceStage(booking.id)}
                    disabled={actionLoading === booking.id || !selectedStage[booking.id]}
                  >
                    {actionLoading === booking.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                  {/* Trigger Email Button */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleTriggerEmail(booking.id, booking)}
                    disabled={actionLoading === `email-${booking.id}`}
                    title="Send test email"
                  >
                    {actionLoading === `email-${booking.id}` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                  </Button>

                  {/* Reset Button */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleReset(booking.id)}
                    disabled={actionLoading === booking.id}
                    title="Reset to initial stage"
                  >
                    {actionLoading === booking.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                  </Button>

                  {/* Cancel Button */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setBookingToCancel(booking)
                      setCancelDialogOpen(true)
                    }}
                    disabled={actionLoading === booking.id || booking.booking_status === 'cancelled'}
                    title="Cancel booking"
                    className="text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>

                  {/* Delete Button */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={actionLoading === booking.id}
                        title="Delete test booking"
                        className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Test Booking?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete this test booking and its stage history.
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(booking.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>

              {/* Expanded Row with Pipeline, Timeline, Upload Links, Job Completion */}
              {expandedRow === booking.id && (
                <TableRow>
                  <TableCell colSpan={7} className="bg-muted/30 p-6">
                    {/* Booking Pipeline */}
                    <div className="mb-6">
                      <BookingPipeline
                        bookingId={booking.id}
                        currentStage={booking.lifecycle_stage as LifecycleStage || 'booked'}
                        onStageUpdate={loadBookings}
                      />
                    </div>

                    {/* Booking Timeline / Audit Trail */}
                    <div className="mb-6">
                      <BookingTimeline bookingId={booking.id} />
                    </div>

                    {/* Upload Links Section - Available from 5-Day Check-In onwards */}
                    {(booking.lifecycle_stage === 'talent_5_day_check_in' ||
                      booking.lifecycle_stage === 'event_day') && (
                      <div className="mb-6">
                        <Card className="border-2 border-indigo-200 bg-indigo-50/50">
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-lg flex items-center gap-2">
                                <Link2 className="h-5 w-5 text-indigo-600" />
                                Talent Upload Links
                              </CardTitle>
                              {editingLinks === booking.id ? (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingLinks(null)
                                      setUploadLinks((prev) => ({
                                        ...prev,
                                        [booking.id]: {
                                          photos: booking.photos_upload_link || '',
                                          videos: booking.videos_upload_link || '',
                                          raws: booking.raws_upload_link || '',
                                        },
                                      }))
                                    }}
                                    disabled={savingLinks === booking.id}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => handleSaveUploadLinks(booking.id)}
                                    disabled={savingLinks === booking.id}
                                    className="gap-2"
                                  >
                                    <Save className="h-4 w-4" />
                                    {savingLinks === booking.id ? 'Saving...' : 'Save Links'}
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingLinks(booking.id)}
                                >
                                  Edit Links
                                </Button>
                              )}
                            </div>
                            <CardDescription>
                              Provide upload links for the talent to submit their media
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div>
                              <label className="text-sm font-medium text-muted-foreground block mb-1">
                                Photos Upload Link
                              </label>
                              {editingLinks === booking.id ? (
                                <input
                                  type="url"
                                  value={uploadLinks[booking.id]?.photos || ''}
                                  onChange={(e) => updateUploadLink(booking.id, 'photos', e.target.value)}
                                  placeholder="https://example.com/upload/photos"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                                />
                              ) : (
                                <p className="text-sm text-foreground break-all">
                                  {booking.photos_upload_link || (
                                    <span className="text-muted-foreground italic">Not set</span>
                                  )}
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="text-sm font-medium text-muted-foreground block mb-1">
                                Videos Upload Link
                              </label>
                              {editingLinks === booking.id ? (
                                <input
                                  type="url"
                                  value={uploadLinks[booking.id]?.videos || ''}
                                  onChange={(e) => updateUploadLink(booking.id, 'videos', e.target.value)}
                                  placeholder="https://example.com/upload/videos"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                                />
                              ) : (
                                <p className="text-sm text-foreground break-all">
                                  {booking.videos_upload_link || (
                                    <span className="text-muted-foreground italic">Not set</span>
                                  )}
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="text-sm font-medium text-muted-foreground block mb-1">
                                RAW Files Upload Link
                              </label>
                              {editingLinks === booking.id ? (
                                <input
                                  type="url"
                                  value={uploadLinks[booking.id]?.raws || ''}
                                  onChange={(e) => updateUploadLink(booking.id, 'raws', e.target.value)}
                                  placeholder="https://example.com/upload/raws"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                                />
                              ) : (
                                <p className="text-sm text-foreground break-all">
                                  {booking.raws_upload_link || (
                                    <span className="text-muted-foreground italic">Not set</span>
                                  )}
                                </p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}

                    {/* Job Completion Form Section */}
                    {jobCompletionForms[booking.id] !== undefined && (() => {
                      const forms = Array.isArray(jobCompletionForms[booking.id])
                        ? jobCompletionForms[booking.id]
                        : jobCompletionForms[booking.id] ? [jobCompletionForms[booking.id]] : []
                      const hasSubmissions = forms.length > 0

                      return (
                        <div className="mb-6">
                          <Card className={`border ${hasSubmissions ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                            <CardHeader className="pb-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setExpandedJobForm(expandedJobForm === booking.id ? null : booking.id)
                                }}
                                className="flex items-center justify-between w-full text-left"
                              >
                                <CardTitle className="text-lg flex items-center gap-2">
                                  <ClipboardCheck className={`h-5 w-5 ${hasSubmissions ? 'text-green-600' : 'text-gray-400'}`} />
                                  Job Completion {forms.length > 1 ? 'Forms' : 'Form'}
                                  {hasSubmissions ? (
                                    <Badge variant="outline" className="ml-2 bg-green-100 text-green-700 border-green-300">
                                      {forms.length} Submitted
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="ml-2 bg-gray-100 text-gray-600 border-gray-300">
                                      Not Submitted
                                    </Badge>
                                  )}
                                </CardTitle>
                                {hasSubmissions && (
                                  expandedJobForm === booking.id ? (
                                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                  ) : (
                                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                  )
                                )}
                              </button>
                              {hasSubmissions && forms.length === 1 && (
                                <CardDescription className="mt-1">
                                  Submitted on {format(new Date(forms[0].created_at), "MMM d, yyyy 'at' h:mm a")}
                                  {forms[0].talent_role && (
                                    <> as <strong>{getRoleLabel(forms[0].talent_role)}</strong></>
                                  )}
                                  {forms[0].submitted_by_user?.full_name && (
                                    <> by {forms[0].submitted_by_user.full_name}</>
                                  )}
                                </CardDescription>
                              )}
                              {hasSubmissions && forms.length > 1 && (
                                <CardDescription className="mt-1">
                                  {forms.map((f: any) => getRoleLabel(f.talent_role || 'photographer')).join(', ')}
                                </CardDescription>
                              )}
                            </CardHeader>

                            {/* Expanded Content - Loop through all forms */}
                            {expandedJobForm === booking.id && hasSubmissions && (
                              <CardContent className="space-y-6 pt-0">
                                {forms.map((form: any, formIndex: number) => {
                                  const roleLabel = getRoleLabel(form.talent_role || 'photographer')
                                  const hasIssues = form.forgot_jpgs || form.forgot_raws || form.missing_required_files || form.insufficient_photos
                                  return (
                                    <div key={form.id || formIndex} className={formIndex > 0 ? 'pt-4 border-t' : ''}>
                                      {forms.length > 1 && (
                                        <div className="mb-3 flex items-center gap-2">
                                          <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-300">
                                            {roleLabel}
                                          </Badge>
                                          <span className="text-xs text-muted-foreground">
                                            {format(new Date(form.created_at), 'MMM d, yyyy')}
                                            {form.submitted_by_user?.full_name && ` by ${form.submitted_by_user.full_name}`}
                                          </span>
                                        </div>
                                      )}
                                      {/* Media Upload Summary */}
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div className="bg-white p-3 rounded-lg border">
                                          <div className="text-xs text-muted-foreground mb-1">Total Files</div>
                                          <div className="text-lg font-semibold">{form.total_files || '—'}</div>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg border">
                                          <div className="text-xs text-muted-foreground mb-1">RAW Uploaded</div>
                                          <div className="flex items-center gap-1">
                                            {form.raw_uploaded ? (
                                              <Check className="h-5 w-5 text-green-600" />
                                            ) : (
                                              <X className="h-5 w-5 text-red-500" />
                                            )}
                                            <span className="font-medium">{form.raw_uploaded ? 'Yes' : 'No'}</span>
                                          </div>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg border">
                                          <div className="text-xs text-muted-foreground mb-1">JPG Uploaded</div>
                                          <div className="flex items-center gap-1">
                                            {form.jpg_uploaded ? (
                                              <Check className="h-5 w-5 text-green-600" />
                                            ) : (
                                              <X className="h-5 w-5 text-red-500" />
                                            )}
                                            <span className="font-medium">{form.jpg_uploaded ? 'Yes' : 'No'}</span>
                                          </div>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg border">
                                          <div className="text-xs text-muted-foreground mb-1">Video Uploaded</div>
                                          <div className="flex items-center gap-1">
                                            {form.video_uploaded ? (
                                              <Check className="h-5 w-5 text-green-600" />
                                            ) : (
                                              <X className="h-5 w-5 text-red-500" />
                                            )}
                                            <span className="font-medium">{form.video_uploaded ? 'Yes' : 'No'}</span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Issues Section */}
                                      {hasIssues && (
                                        <div className="mt-3 bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                                          <div className="flex items-center gap-2 mb-2">
                                            <AlertTriangle className="h-5 w-5 text-amber-600" />
                                            <span className="font-semibold text-amber-900">Issues Reported</span>
                                          </div>
                                          <ul className="space-y-1 text-sm text-amber-800">
                                            {form.forgot_jpgs && <li>• Forgot to shoot JPGs</li>}
                                            {form.forgot_raws && <li>• Forgot to shoot RAWs</li>}
                                            {form.missing_required_files && <li>• Missing required files</li>}
                                            {form.insufficient_photos && <li>• Did not shoot 100 photos per hour</li>}
                                          </ul>
                                        </div>
                                      )}

                                      {/* Overtime Section */}
                                      {form.overtime_requested && (
                                        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
                                          <div className="flex items-center gap-2">
                                            <Clock className="h-5 w-5 text-blue-600" />
                                            <span className="font-semibold text-blue-900">Overtime Requested</span>
                                            {form.overtime_hours && (
                                              <Badge variant="outline" className="ml-2 bg-blue-100 text-blue-700 border-blue-300">
                                                {form.overtime_hours} hour{form.overtime_hours !== 1 ? 's' : ''}
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Wedding Notes */}
                                      {form.wedding_notes && (
                                        <div className="mt-3">
                                          <div className="text-sm font-medium text-muted-foreground mb-1">Wedding Notes</div>
                                          <div className="bg-white p-3 rounded-lg border text-sm whitespace-pre-wrap">
                                            {form.wedding_notes}
                                          </div>
                                        </div>
                                      )}

                                      {/* Feedback */}
                                      {form.feedback && (
                                        <div className="mt-3">
                                          <div className="text-sm font-medium text-muted-foreground mb-1">Feedback for Love & Photos</div>
                                          <div className="bg-white p-3 rounded-lg border text-sm whitespace-pre-wrap">
                                            {form.feedback}
                                          </div>
                                        </div>
                                      )}

                                      {/* Upload Links at submission */}
                                      {(form.photos_upload_link || form.videos_upload_link || form.raws_upload_link) && (
                                        <div className="mt-3">
                                          <div className="text-sm font-medium text-muted-foreground mb-1">Upload Links (at submission)</div>
                                          <div className="bg-white p-3 rounded-lg border text-sm space-y-1">
                                            {form.photos_upload_link && (
                                              <div className="break-all">
                                                <span className="text-muted-foreground">Photos:</span>{' '}
                                                <a href={form.photos_upload_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                                  {form.photos_upload_link}
                                                </a>
                                              </div>
                                            )}
                                            {form.videos_upload_link && (
                                              <div className="break-all">
                                                <span className="text-muted-foreground">Videos:</span>{' '}
                                                <a href={form.videos_upload_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                                  {form.videos_upload_link}
                                                </a>
                                              </div>
                                            )}
                                            {form.raws_upload_link && (
                                              <div className="break-all">
                                                <span className="text-muted-foreground">RAWs:</span>{' '}
                                                <a href={form.raws_upload_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                                  {form.raws_upload_link}
                                                </a>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </CardContent>
                            )}
                          </Card>
                        </div>
                      )
                    })()}
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          ))}
        </TableBody>
      </Table>

      {/* Cancel Booking Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Booking?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the booking for{' '}
              <span className="font-semibold">{bookingToCancel?.customer?.full_name || 'Unknown'}</span>{' '}
              on {bookingToCancel?.event_date ? format(parseDateOnly(bookingToCancel.event_date), 'MMM d, yyyy') : ''}.
              Cancellation emails will be sent to both the customer and photographer.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              Cancellation Reason <span className="text-red-500">*</span>
            </label>
            <Textarea
              placeholder="Please provide a reason for cancellation..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setCancelReason('')
                setBookingToCancel(null)
              }}
            >
              Go Back
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={cancelling || !cancelReason.trim()}
              className="text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700"
            >
              {cancelling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Confirm Cancel'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
